<script setup>

/**
 * Presentational recovery table for soft-deleted clients. Caller fetches, restores, and notifies;
 * `restoreClient` resolves whether to drop the row.
 * Billing is read from the auth store, not props, because it changes after each restore while
 * the dialog stays open. Stays open, so several clients can be restored in a row; no bulk restore,
 * deliberately.
 */

import { i18n } from 'boot/i18n';
import { storeToRefs } from 'pinia';
import { useClients } from 'src/composables/clients';
import { useTime } from 'src/composables/time';
import { CLIENT_RECOVERY_WINDOW_DAYS, CLIENT_STATUS } from 'src/constants/clients';
import {
  HrAdaptiveFormDialog, HrDataTable, HrDismissBtn, HrErrorBanner, HrSubtleBtn, HrWarningBanner,
} from 'src/design-system';
import { auth as useAuthStore } from 'src/store/auth';
import { computed, ref } from 'vue';

defineOptions({ name: 'RestoreClientsDialog' });

const props = defineProps({
  softDeletedClients: {
    type: Array,
    required: true,
  },
  restoreClient: {
    type: Function,
    required: true,
  },
});

const { formatDate } = useTime();
const { getRecoveryDaysRemaining, isRestorable } = useClients();

const dialog = ref(null);
const restoringId = ref(null);

const { isSubscriptionUnavailable, getAvailableLicenses } = storeToRefs(useAuthStore());

function daysRemaining(client) {
  return getRecoveryDaysRemaining(client.deletedOn);
} // daysRemaining()

// A client past the window can still be in Firestore lingers until the nightly deletedClientsCleanup,
// but is no longer restorable.
const rows = ref(props.softDeletedClients
  .map((client) => ({
    ...client,
    deletedOn: client.deleteTimestamp?.toDate() ?? null,
  }))
  .filter((client) => isRestorable(client.deletedOn)));

const showNoSubscription = computed(() => isSubscriptionUnavailable.value);

const showLicenseNeeded = computed(() => !isSubscriptionUnavailable.value
  && getAvailableLicenses.value('client') < 1
  && rows.value.some(({ status }) => status === CLIENT_STATUS.ACTIVE));

function formatDeletedOn(client) {
  return client.deletedOn ? formatDate(client.deletedOn, 'yyyy-MM-dd') : '';
} // formatDeletedOn()

const columns = computed(() => [
  {
    name: 'clientName',
    label: i18n.t('clients.modals.restoreClients.tableHeaderClientName'),
    field: 'clientName',
    align: 'left',
  },
  {
    name: 'deletedOn',
    label: i18n.t('clients.modals.restoreClients.tableHeaderDeletedOn'),
    field: formatDeletedOn,
    align: 'left',
  },
  {
    name: 'daysRemaining',
    label: i18n.t('clients.modals.restoreClients.tableHeaderDaysRemaining'),
    field: daysRemaining,
    align: 'left',
  },
]);

async function handleRestore(client) {
  restoringId.value = client.id;
  try {
    const restored = await props.restoreClient(client);
    if (restored) {
      rows.value = rows.value.filter(({ id }) => id !== client.id);
    }
  } finally {
    restoringId.value = null;
  }
} // handleRestore()

function hide() {
  dialog.value?.hide();
}

defineExpose({
  show: () => dialog.value?.show(),
  hide,
});
</script>

<template>
  <hr-adaptive-form-dialog
    ref="dialog"
    :title="i18n.t('clients.modals.restoreClients.dialogRestoreClientsTitle')"
    size="md"
  >
    <p>
      {{ i18n.t('clients.modals.restoreClients.dialogRestoreClientsMessage', {days: CLIENT_RECOVERY_WINDOW_DAYS}) }}
    </p>

    <hr-error-banner
      v-if="showNoSubscription"
      :message="i18n.t('clients.modals.editClient.noSubscription')"
      class="q-mb-md"
    />
    <hr-warning-banner
      v-else-if="showLicenseNeeded"
      :message="i18n.t('clients.modals.restoreClients.bannerLicenseNeeded')"
      class="q-mb-md"
    />

    <hr-data-table
      :columns="columns"
      :no-data-label="i18n.t(
        'clients.modals.restoreClients.textNoDeletedClients',
        { days: CLIENT_RECOVERY_WINDOW_DAYS },
      )"
      :rows="rows"
      row-key="id"
    >
      <template #row-actions="{ row }">
        <hr-subtle-btn
          :disable="restoringId !== null || (showNoSubscription && row.status === CLIENT_STATUS.ACTIVE)"
          :label="i18n.t('clients.modals.restoreClients.buttonRestore')"
          @click="handleRestore(row)"
        />
      </template>
    </hr-data-table>

    <template #actions>
      <hr-dismiss-btn
        :label="i18n.t('common.buttonClose')"
        @click="hide"
      />
    </template>
  </hr-adaptive-form-dialog>
</template>
